import { SupabaseSong } from '../types/supabase';
import SpotifyApi from 'spotify-types';

class SpotifyService {
  private player: Spotify.Player | null = null;
  private deviceId: string | null = null;
  private accessToken: string | null = null;
  private currentDuration = 0;
  private currentProgress = 0;
  private isPlayerReady = false;
  private initializationPromise: Promise<void> | null = null;
  private isInitializing = false;
  private progressInterval: number | null = null;

  constructor() {
    // Don't initialize in constructor anymore
  }

  private async verifyScopes(token: string): Promise<boolean> {
    try {
      console.log('Starting token verification...');

      // Check if we're on a mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      console.log('Device type:', isMobile ? 'Mobile' : 'Desktop');

      // Check token validity and get current user's info
      const userResponse = await fetch('https://api.spotify.com/v1/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!userResponse.ok) {
        const errorText = await userResponse.text();
        console.error('Failed to verify token:', errorText);
        return false;
      }

      console.log('Token is valid, checking player access...');

      // Check if we can access player
      const playerResponse = await fetch('https://api.spotify.com/v1/me/player', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // 204 or 404 are both ok - they just mean no active player
      if (![200, 204, 404].includes(playerResponse.status)) {
        console.error('Failed to access player:', {
          status: playerResponse.status,
          statusText: playerResponse.statusText,
        });
        return false;
      }

      // Verify we have the required scopes
      const scopesResponse = await fetch('https://api.spotify.com/v1/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!scopesResponse.ok) {
        console.error('Failed to verify scopes:', await scopesResponse.text());
        return false;
      }

      const userData = await scopesResponse.json();
      console.log('User data:', userData);

      // Check if we can access the player endpoints
      const testPlayResponse = await fetch('https://api.spotify.com/v1/me/player/devices', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!testPlayResponse.ok) {
        console.error('Missing required scopes for playback:', await testPlayResponse.text());
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error verifying token:', error);
      return false;
    }
  }

  private initializePlayer(): Promise<void> {
    if (this.isInitializing) {
      console.log('Player initialization already in progress...');
      if (!this.initializationPromise) {
        throw new Error('Initialization promise is missing');
      }
      return this.initializationPromise;
    }

    if (this.player) {
      console.log('Disconnecting existing player...');
      this.player.disconnect();
      this.player = null;
      this.deviceId = null;
      this.isPlayerReady = false;
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    }

    this.isInitializing = true;

    this.initializationPromise = new Promise((resolve, reject) => {
      let initTimeout: number;
      let hasConnected = false;
      let authErrorOccurred = false;

      const cleanup = () => {
        this.isInitializing = false;
        clearTimeout(initTimeout);
      };

      // Define the handler before loading the script
      window.onSpotifyWebPlaybackSDKReady = () => {
        console.log('Creating Spotify player...');

        this.player = new window.Spotify.Player({
          name: 'TrackToss Player',
          getOAuthToken: (cb: (token: string) => void) => {
            if (this.accessToken) {
              cb(this.accessToken);
            }
          },
          volume: 1.0,
          enableMediaSession: true, // Enable media session for better mobile support
        });

        this.player.addListener('ready', async ({ device_id }: { device_id: string }) => {
          console.log('Player ready with device ID:', device_id);
          this.deviceId = device_id;
          this.isPlayerReady = true;
          hasConnected = true;

          // Set initial volume and ensure device is active
          try {
            await this.setVolume(100);
            // Activate the device
            const response = await fetch('https://api.spotify.com/v1/me/player', {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.accessToken}`,
              },
              body: JSON.stringify({
                device_ids: [device_id],
                play: false,
              }),
            });

            if (!response.ok && response.status !== 204) {
              console.error(
                'Failed to activate device during initialization:',
                await response.text(),
              );
            }
          } catch (error) {
            console.error('Failed to set initial volume or activate device:', error);
          }

          cleanup();
          resolve();
        });

        this.player.addListener('not_ready', ({ device_id }: { device_id: string }) => {
          console.log('Device ID has gone offline:', device_id);
          this.isPlayerReady = false;
          this.deviceId = null;
          if (this.progressInterval) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
          }
        });

        this.player.addListener('initialization_error', ({ message }: { message: string }) => {
          console.error('Failed to initialize player:', message);
          this.isPlayerReady = false;
          this.deviceId = null;
          cleanup();
          reject(new Error(message));
        });

        this.player.addListener('authentication_error', ({ message }: { message: string }) => {
          console.log('Authentication check in progress:', message);
          authErrorOccurred = true;
        });

        this.player.addListener('playback_error', ({ message }: { message: string }) => {
          console.error('Failed to perform playback:', message);
        });

        this.player.addListener('player_state_changed', (state: Spotify.PlaybackState) => {
          if (state) {
            this.currentDuration = state.duration / 1000;
            this.currentProgress = state.position / 1000;

            if (!this.progressInterval) {
              this.progressInterval = window.setInterval(() => {
                if (this.currentProgress < this.currentDuration) {
                  this.currentProgress += 1;
                } else {
                  clearInterval(this.progressInterval ?? undefined);
                  this.progressInterval = null;
                }
              }, 1000);
            }
          } else {
            if (this.progressInterval) {
              clearInterval(this.progressInterval);
              this.progressInterval = null;
            }
            this.currentProgress = 0;
            this.currentDuration = 0;
          }
        });

        console.log('Connecting to Spotify...');
        this.player
          .connect()
          .then((success: boolean) => {
            if (success) {
              console.log('Successfully connected to Spotify!');
            } else {
              console.error('Failed to connect to Spotify');
              cleanup();
              reject(new Error('Failed to connect to Spotify'));
            }
          })
          .catch((error: Error) => {
            console.error('Failed to connect player:', error);
            cleanup();
            reject(error);
          });
      };

      // Set a timeout for initialization
      initTimeout = window.setTimeout(() => {
        if (hasConnected) {
          console.log('Player connected successfully despite timeout');
          return;
        }
        cleanup();
        if (authErrorOccurred) {
          reject(new Error('Failed to authenticate with Spotify - please check your token scopes'));
        } else {
          reject(new Error('Player initialization timed out'));
        }
      }, 15000);

      // Load the script after defining the handler
      const script = document.createElement('script');
      script.src = 'https://sdk.scdn.co/spotify-player.js';
      script.async = true;

      script.onerror = () => {
        cleanup();
        reject(new Error('Failed to load Spotify Web Playback SDK'));
      };

      document.body.appendChild(script);
    });

    return this.initializationPromise;
  }

  public async setAccessToken(token: string) {
    try {
      console.log('Setting access token...');

      // Verify token scopes first
      const isValid = await this.verifyScopes(token);
      if (!isValid) {
        console.error('Invalid token or missing required scopes');
        return false;
      }

      this.accessToken = token;

      console.log('Initializing player...');
      await this.initializePlayer();

      console.log('Player initialization complete:', {
        isReady: this.isPlayerReady,
        hasDeviceId: !!this.deviceId,
        hasToken: !!this.accessToken,
      });

      return this.isPlayerReady;
    } catch (error) {
      console.error('Failed to set access token:', error);
      this.accessToken = null;
      this.isPlayerReady = false;
      this.deviceId = null;
      return false;
    }
  }

  public async play(song: SupabaseSong, resumeFromPause = false): Promise<boolean> {
    if (!this.isPlayerReady || !this.deviceId || !this.accessToken) {
      console.error('Player not ready:', {
        isPlayerReady: this.isPlayerReady,
        deviceId: this.deviceId,
        hasAccessToken: !!this.accessToken,
      });
      return false;
    }

    try {
      console.log('Starting playback for song:', {
        title: song.title,
        url: song.url,
        spotify_id: song.spotify_id
      });

      // Get current available devices
      const devicesResponse = await fetch('https://api.spotify.com/v1/me/player/devices', {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      });

      if (!devicesResponse.ok) {
        console.error('Failed to get devices:', await devicesResponse.text());
        return false;
      }

      const devicesData = await devicesResponse.json();
      console.log('Available devices:', devicesData.devices);
      
      const ourDevice = devicesData.devices.find((d: SpotifyApi.Device) => d.id === this.deviceId);
      console.log('Our device:', ourDevice);

      if (!ourDevice) {
        console.log('Our device not found in available devices, refreshing device ID...');
        // Try to reconnect the player
        await this.initializePlayer();
        if (!this.deviceId) {
          console.error('Failed to get new device ID');
          return false;
        }
      }

      console.log('Activating device...');
      // First ensure our device is active
      const activateResponse = await fetch('https://api.spotify.com/v1/me/player', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.accessToken}`,
        },
        body: JSON.stringify({
          device_ids: [this.deviceId],
          play: false, // Don't start playing yet
        }),
      });

      if (!activateResponse.ok && activateResponse.status !== 204) {
        console.error('Failed to activate device:', await activateResponse.text());
        return false;
      }

      // Wait for device activation
      console.log('Waiting for device activation...');
      await new Promise((resolve) => setTimeout(resolve, 5000));

      // Verify device is active
      const playerState = await fetch('https://api.spotify.com/v1/me/player', {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      });

      if (!playerState.ok) {
        console.error('Failed to verify device state:', await playerState.text());
        return false;
      }

      const playerStateData = await playerState.json();
      console.log('Current player state:', playerStateData);

      // If the active device is different from our device, try to activate our device again
      if (playerStateData.device?.id !== this.deviceId) {
        console.log('Active device different from our device, reactivating...');
        const reactivateResponse = await fetch('https://api.spotify.com/v1/me/player', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.accessToken}`,
          },
          body: JSON.stringify({
            device_ids: [this.deviceId],
            play: false,
          }),
        });

        if (!reactivateResponse.ok && reactivateResponse.status !== 204) {
          console.error('Failed to reactivate device:', await reactivateResponse.text());
          return false;
        }

        // Wait again for device activation
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      // Try to play the song with retries
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          console.log(`Starting playback attempt ${retryCount + 1}...`);

          // First, try to load the track
          const loadResponse = await fetch(
            `https://api.spotify.com/v1/me/player/play?device_id=${this.deviceId}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.accessToken}`,
              },
              body: JSON.stringify({
                uris: [song.url],
                position_ms: resumeFromPause ? this.currentProgress * 1000 : 0,
              }),
            },
          );

          if (!loadResponse.ok) {
            const errorText = await loadResponse.text();
            console.error(`Playback attempt ${retryCount + 1} failed:`, errorText);

            if (retryCount < maxRetries - 1) {
              // Wait before retrying
              await new Promise((resolve) => setTimeout(resolve, 2000));
              retryCount++;
              continue;
            }
            return false;
          }

          // Wait a bit to ensure playback has started
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Verify playback has started
          const verifyResponse = await fetch('https://api.spotify.com/v1/me/player', {
            headers: {
              Authorization: `Bearer ${this.accessToken}`,
            },
          });

          if (verifyResponse.ok) {
            const verifyData = (await verifyResponse.json()) as SpotifyApi.CurrentlyPlaying;
            console.log('Playback verification:', {
              isPlaying: verifyData.is_playing,
              currentUri: verifyData.item?.uri,
              expectedUri: song.url
            });
            
            if (verifyData.is_playing && verifyData.item?.uri === song.url) {
              console.log('Playback verified successfully');
              return true;
            }
          }

          if (retryCount < maxRetries - 1) {
            retryCount++;
            continue;
          }
        } catch (error) {
          console.error(`Error during playback attempt ${retryCount + 1}:`, error);
          if (retryCount < maxRetries - 1) {
            retryCount++;
            continue;
          }
          throw error;
        }
      }

      console.error('All playback attempts failed');
      return false;
    } catch (error) {
      console.error('Error playing track:', error);
      return false;
    }
  }

  public async pause() {
    if (!this.deviceId || !this.accessToken) {
      throw new Error('Spotify player not ready');
    }

    const response = await fetch(
      `https://api.spotify.com/v1/me/player/pause?device_id=${this.deviceId}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error('Failed to pause song');
    }
  }

  public async setVolume(volume: number) {
    if (!this.deviceId || !this.accessToken) {
      throw new Error('Spotify player not ready');
    }

    const response = await fetch(
      `https://api.spotify.com/v1/me/player/volume?volume_percent=${volume}&device_id=${this.deviceId}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error('Failed to set volume');
    }
  }

  public async seekTo(position: number) {
    if (!this.deviceId || !this.accessToken) {
      throw new Error('Spotify player not ready');
    }

    const response = await fetch(
      `https://api.spotify.com/v1/me/player/seek?position_ms=${position * 1000}&device_id=${this.deviceId}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error('Failed to seek to position');
    }
  }

  public getCurrentDuration(): number {
    return this.currentDuration;
  }

  public getCurrentProgress(): number {
    return this.currentProgress;
  }

  public async search(query: string): Promise<SupabaseSong[]> {
    if (!this.accessToken) {
      throw new Error('No access token available');
    }

    try {
      const response = await fetch(
        `https://api.spotify.com/v1/search?q=${encodeURIComponent(query)}&type=track&limit=10`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to search Spotify');
      }

      const data = (await response.json()) as SpotifyApi.SearchContent;
      if (!data.tracks || !data.tracks.items) {
        throw new Error('Invalid response from Spotify');
      }

      return data.tracks.items.map((track) => ({
        id: track.id,
        title: track.name,
        artist: track.artists.map((artist) => artist.name).join(', '),
        url: track.uri,
        playlist_id: '', // This will be set when added to a playlist
        votes: 0,
        added_by: '', // This will be set when added to a playlist
        created_at: new Date().toISOString(),
        album_artwork: track.album.images[0]?.url || null,
        played: false,
        spotify_id: track.id,
        is_currently_playing: false,
        play_progress: 0,
        played_at: null,
        last_updated: null
      }));
    } catch (error) {
      console.error('Error searching Spotify:', error);
      throw error;
    }
  }
}

export const spotifyService = new SpotifyService();
